import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import axiosWrapper from "@AxiosWrapper";
import { portalPurchaseUnitsUrl } from "@Services/dataManager";
import type { GetPurchaseUnitsResponse } from "@ServerTypes/units/units.schema";
type PurchaseUnitItem = NonNullable<GetPurchaseUnitsResponse["result"]>[number];
import { TextField } from "@mui/material";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import IconComponent from "@CubeComponents/icons";
import BoqItemQuantityChip from "./BoqItemQuantityChip";
import { getEditBOQItemsTabs, getEstimateNumberValueBasedOnSettings } from "@Actions/estimation";
import { useSelector } from "react-redux";
import { useTranslation } from "@i18n";
import { useEffect, useState } from "react";
import type { RootState } from "src/reducers";
import type { EstimateCurrency, EstimateData as EstimateDataState } from "@ServerTypes/estimates/estimates.schema";

type CurrencyDetails = EstimateCurrency;

type BoqItemDetails = {
  code?: string;
  description?: string;
  manualQuantity?: number | string;
  quantityFromTakeoffs?: number | string;
  purchaseUnit?: string;
  hasChild?: boolean;
  rate?: number;
  amount?: number | string;
  isRateAnalysisEnable?: boolean;
};

/**
 * onChange args. The parent (`BoqItemEditPopUp`) dispatches multiple input shapes
 * (NumericTextBox, AutoComplete, TextField, rate-analysis payloads) through this
 * function — see `UpdateItemDetailsInput` there.
 */
type NumericArgs = {
  value: number | null;
  previousValue?: number | null;
  event?: { stopPropagation?: () => void };
  target: { value: string; name?: string };
};
type UpdateItemDetailsArgs =
  | NumericArgs
  | { value: PurchaseUnitItem }
  | { target: { value: string; name?: string; itemData?: { name?: string } } }
  | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

type BoqItemBasicDetailsProps = {
  itemCurrentDetails: BoqItemDetails;
  currencyDetails: CurrencyDetails;
  setCurrentTab: (tab: string) => void;
  updateItemDetails: (args: UpdateItemDetailsArgs, field?: string, checkNumberIsPositive?: boolean) => void;
  isActionPermission: boolean;
};

const BoqItemBasicDetails = ({
  itemCurrentDetails,
  currencyDetails,
  setCurrentTab,
  updateItemDetails,
  isActionPermission,
}: BoqItemBasicDetailsProps) => {
  const estimateData = useSelector((state: RootState) => state.estimateData) as EstimateDataState;
  const { t } = useTranslation();
  const EditBOQItemsTabs = getEditBOQItemsTabs();
  const currencySymbol = currencyDetails?.selectedSymbol?.name ?? "";
  const [purchaseUnitsOptions, setPurchaseUnitsOptions] = useState<PurchaseUnitItem[]>([]);

  useEffect(() => {
    const fetchPurchaseUnits = async () => {
      const response = await axiosWrapper.post<GetPurchaseUnitsResponse>(portalPurchaseUnitsUrl, {
        value: { params: {} },
      });
      if (response.data?.result) {
        setPurchaseUnitsOptions(response.data.result);
      }
    };
    fetchPurchaseUnits();
  }, []);
  return (
    <div className="flex flex-col py-5 gap-6 w-full">
      <div className="flex items-center">
        <div className="text-base font-semibold flex flex-1">{t("Code")}</div>
        <div className="flex flex-4">
          <TextFieldComponent className="w-full" disabled placeholder="" value={itemCurrentDetails.code} />
        </div>
      </div>
      <div className="flex">
        <div className="text-base font-semibold flex flex-1">{t("Description")}</div>
        <div className="flex flex-4">
          <TextField
            id="outlined-multiline-static"
            value={itemCurrentDetails.description}
            multiline
            rows={4}
            maxRows={5}
            placeholder={t("Enter Description")}
            name="description"
            onChange={updateItemDetails}
            inputProps={{ maxLength: 3000 }}
            fullWidth
            disabled={!isActionPermission || Boolean(itemCurrentDetails.code)}
          />
        </div>
      </div>

      <div className="flex items-center">
        <div className="text-base font-semibold flex flex-1">{t("Quantity")}</div>
        <div className="w-full flex flex-4 gap-6 justify-between items-center">
          <BoqItemQuantityChip
            manualQuantity={itemCurrentDetails.manualQuantity}
            quantityFromTakeoffs={itemCurrentDetails.quantityFromTakeoffs}
          />
          <IconComponent
            name="openInViewer"
            className="cursor-pointer"
            onClick={() => setCurrentTab(EditBOQItemsTabs.QUANTITY.value)}
            width="1.25rem"
            height="1.25rem"
          />
        </div>
      </div>

      <div className="flex items-center">
        <div className="text-base font-semibold flex flex-1 gap-1 items-center">
          <span>{t("Unit")}</span>
          <IconComponent
            name="details"
            position="right"
            toolTipContent={t("Purchase Unit")}
            width="1.25rem"
            height="1.25rem"
          />
        </div>
        <div className="flex flex-4 gap-6 items-center">
          <AutoComplete
            options={purchaseUnitsOptions}
            disableClearable
            valueField="_id"
            getOptionLabel={(option) => option?.nameWithSymbol || ""}
            value={itemCurrentDetails.purchaseUnit ?? undefined}
            disabled={
              Boolean(itemCurrentDetails.code) ||
              itemCurrentDetails.hasChild ||
              Boolean(itemCurrentDetails.quantityFromTakeoffs) ||
              !isActionPermission
            }
            onChange={(_event, newValue) => {
              if (newValue?._id) {
                updateItemDetails({ value: newValue }, "purchaseUnit");
              }
            }}
            sx={{ width: "100%" }}
            renderInput={(params) => <TextFieldComponent {...params} />}
          />
          <IconComponent
            name="openInViewer"
            className="cursor-pointer"
            onClick={() => setCurrentTab(EditBOQItemsTabs.UNIT.value)}
            width="1.25rem"
            height="1.25rem"
          />
        </div>
      </div>

      <div className="flex items-center">
        <div className="text-base font-semibold flex flex-1 gap-1">
          <span>{t("Rate")}</span>
          <span>({currencySymbol})</span>
        </div>
        <div className="flex flex-4 gap-6 items-center">
          <NumericTextBoxComponent
            placeholder={t("Enter Rate")}
            value={itemCurrentDetails.rate}
            min={0}
            onChange={(args: NumericArgs) => updateItemDetails(args, "rate")}
            disabled={
              !isActionPermission ||
              itemCurrentDetails.hasChild ||
              Boolean(itemCurrentDetails.code) ||
              Boolean(itemCurrentDetails.isRateAnalysisEnable)
            }
          />
          <IconComponent
            name="openInViewer"
            className="cursor-pointer"
            onClick={() => setCurrentTab(EditBOQItemsTabs.RATE.value)}
            width="1.25rem"
            height="1.25rem"
          />
        </div>
      </div>

      <div className="flex items-center">
        <div className="text-base font-semibold flex flex-1 gap-1">
          <span>{t("Amount")}</span>
          <span>({currencySymbol})</span>
        </div>
        <div className="flex flex-4 gap-6 items-center">
          <TextFieldComponent
            className="w-full"
            disabled
            placeholder=""
            value={getEstimateNumberValueBasedOnSettings(
              parseFloat(String(itemCurrentDetails.amount ?? 0)),
              estimateData,
              "amount",
            )}
          />
          <IconComponent
            name="openInViewer"
            className="cursor-pointer"
            onClick={() => setCurrentTab(EditBOQItemsTabs.AMOUNT.value)}
            width="1.25rem"
            height="1.25rem"
          />
        </div>
      </div>
    </div>
  );
};

export default BoqItemBasicDetails;
